import { useAnalytics } from '#~/analytics/hooks/index.js';
import { createDeviceIdentificationTracker } from '#~/analytics/domains/device-identification.js';
import { Sel, STag } from '#~/stag/tag.js';
import type { BrandRow } from './search.js';
import {
  findOther,
  getBrandRows,
  getBrandRowSearchKeys,
  getCombinedRows,
  isOtherName,
  NotListedActionLabel,
  NotListedLabel
} from './search.js';
import type { BrandProps } from './types.js';
import { useNotListedFlow } from './not-listed.jsx';
import { Combobox } from '@studio/design-system/combobox';
import { Tag } from '@studio/design-system/tag';
import { useLatestFn } from '@studio/react-utils/hooks';
import { isEmpty } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { memo, useMemo, useRef, useState } from 'react';

/** Stable key per row: brands and combined rows live in one listbox. */
const getRowKey = (row: BrandRow): string =>
  row.kind === 'brand' ? `brand:${row.brand.id}` : `model:${row.model.id}`;

/**
 * What the input shows once a row is committed. A combined row commits its
 * brand, so it reads as the brand; the generic brand reads "Not listed" (C3).
 */
const getRowLabel = (row: BrandRow): string =>
  isOtherName(row.brand.name) ? NotListedLabel : row.brand.name;

/** Per-row selector: `brand-option:<brandId>` or `brand-option:<brandId>.<modelId>`. */
const getRowAttributes = (row: BrandRow) =>
  STag(
    Sel.ConfigVideoDeviceBrandModel,
    'brand-option',
    row.kind === 'brand' ? row.brand.id : `${row.brand.id}.${row.model.id}`
  );

/**
 * Visible row content; combined rows say both halves (B3 "Sony · SRG-A40").
 * ENG-4924: a combined row whose model we recognise but cannot drive carries
 * the same "Not yet supported" tag as the model picker's rows.
 */
const renderRow = (row: BrandRow): ReactElement =>
  row.kind === 'brand' ? (
    <span>{getRowLabel(row)}</span>
  ) : (
    <div className='flex w-full items-center justify-between gap-8'>
      <span>
        {row.brand.name} · {row.model.name}
      </span>
      {row.model.notYetSupported && (
        <Tag
          className='px-12 py-6 text-sm font-medium'
          message='Not yet supported'
          severity='info'
          data-stag='config.video-device.brand-model.not-yet-supported-tag'
        />
      )}
    </div>
  );

/**
 * `<Brand>` component — type-ahead brand search (ENG-4831).
 *
 * Replaces the brand `Select`: every brand A→Z minus the generic "Other"
 * brand, combined "Brand · Model" rows while searching (when the caller opts
 * in with `models` + `onModelSelect`), and a fixed "My camera isn't listed"
 * trailing action that replaces the old "Other - brand is not listed" row.
 *
 * Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — input,
 * rows and the trailing action carry `brand-input` / `brand-option:<id>` /
 * `not-listed:brand` STags.
 */
export const Brand = memo(
  ({
    brands,
    confirmOther = true,
    initialQuery,
    models,
    onIdentified,
    onModelSelect,
    onNotListed,
    onQueryChange,
    selection,
    size,
    ...props
  }: BrandProps): ReactElement => {
    const { track } = useAnalytics();
    const [query, setQuery] = useState(initialQuery ?? '');

    // ENG-4832: "Back returns to the list, keeping the search text". The
    // combobox drops its query back to the committed brand's label when the
    // trailing action (or a row) commits, so the query is snapshotted when a
    // detour opens and the combobox is remounted with it as `initialQuery`
    // when the detour ends with Back.
    const [magicNumber, setMagicNumber] = useState(0);
    const [restoredQuery, setRestoredQuery] = useState(initialQuery);
    const detourQuery = useRef<string | undefined>(undefined);
    const resetInput = useLatestFn((keepQuery?: string) => {
      setRestoredQuery(keepQuery);
      setMagicNumber((µ) => µ + 1);
    });
    const restoreInput = useLatestFn(() => resetInput(detourQuery.current));

    // The caller learns about typing here (not from `models`), so it can start
    // warming the registry cache exactly when combined rows first need it.
    const handleQueryChange = useLatestFn((nextQuery: string) => {
      setQuery(nextQuery);
      onQueryChange?.(nextQuery);
    });

    const brandRows = useMemo(
      () => getBrandRows(brands, selection.value),
      [brands, selection.value]
    );

    // B3 rows exist only while the user is typing, so an empty query shows
    // brands alone (B1) instead of a few hundred models.
    const combinedRows = useMemo(
      () => (onModelSelect ? getCombinedRows(query, brands, models) : []),
      [brands, models, onModelSelect, query]
    );
    const items = useMemo(() => [...brandRows, ...combinedRows], [brandRows, combinedRows]);

    // Commit the generic "Other" brand: what "not listed" means to this picker
    // when the caller has no identification flow of its own.
    const commitOther = useLatestFn(() => {
      const other = findOther(brands);

      if (other && other.id !== selection.value) {
        selection(other.id);
      }
    });

    // ENG-4832: replaces the "Enter Make & Model" prompt with C1/C2.
    const flow = useNotListedFlow({
      brands,
      models,
      onBack: restoreInput,
      onContinue: (identification) => {
        // ENG-4832: the typed text and `identified_by` travel with the generic
        // brand so the caller persists them (settings: StudioDevice 13–15).
        onIdentified?.(identification);
        commitOther();
      },
      onDidYouMean: ({ brandId, modelId }) => {
        const model = models?.[brandId]?.items[modelId];

        if (model && onModelSelect) {
          onModelSelect(model);
        } else if (brandId !== selection.value) {
          selection(brandId);
        }
      }
    });

    // Caller flow first (the add wizard). Surfaces that cannot show a dialog
    // over themselves (`confirmOther` false: the onboarding top-layer dialog,
    // the guided RTSP screen) commit the generic brand directly, as before.
    const handleNotListed = useLatestFn((nextQuery: string) => {
      if (onNotListed) {
        onNotListed(nextQuery);
      } else if (confirmOther) {
        detourQuery.current = nextQuery;
        flow.open({ initial: { brand: nextQuery, model: '' } });
      } else {
        commitOther();
      }
    });

    const handleChange = useLatestFn((row: BrandRow) => {
      if (row.kind === 'model') {
        // ENG-4924: a "Not yet supported" combined row is never a selection.
        // Picking it opens C2 prefilled with both names; Continue saves the
        // generic Other/Other with the typed names and
        // `not_yet_supported_picked`, Back/close restores the search. A caller
        // with its own flow (`onNotListed`, the add wizard) handles it itself.
        if (row.model.notYetSupported && !onNotListed && confirmOther) {
          detourQuery.current = query;
          // The combobox already shows the picked row's brand; drop it, since
          // nothing was selected.
          resetInput();
          flow.open({
            initial: { brand: row.brand.name, model: row.model.name },
            openedFrom: 'not_yet_supported_pick',
            step: 'c2'
          });

          return;
        }

        // The add wizard diverts a tagged row to its own C2 and selects nothing, so the
        // picked row's label must not stay in the field after Back.
        if (row.model.notYetSupported && onNotListed) resetInput();

        onModelSelect?.(row.model);

        return;
      }

      if (row.brand.id !== selection.value) {
        selection(row.brand.id);
      }
    });

    const handleNoMatches = useLatestFn((nextQuery: string) => {
      // ENG-4831: the brand field is not scoped to a brand, so brand_name is ''.
      createDeviceIdentificationTracker(track).trackDeviceSearchNoMatches({
        search_text: nextQuery,
        search_field: 'brand',
        brand_name: ''
      });
    });

    const trailingAction = useMemo(
      () => ({
        attributes: STag(Sel.ConfigVideoDeviceBrandModel, 'not-listed', 'brand'),
        label: NotListedActionLabel,
        onSelect: handleNotListed
      }),
      [handleNotListed]
    );

    return (
      <>
        <Combobox<BrandRow>
          {...props}
          {...STag(Sel.ConfigVideoDeviceBrandModel, 'brand-input')}
          getKey={getRowKey}
          getLabel={getRowLabel}
          getOptionAttributes={getRowAttributes}
          getSearchKeys={getBrandRowSearchKeys}
          items={items}
          key={`brand-combobox-gen-${magicNumber}`}
          onChange={handleChange}
          onNoMatches={handleNoMatches}
          onQueryChange={handleQueryChange}
          placeholder={isEmpty(brands) ? 'Loading brands…' : 'Search brands'}
          renderItem={renderRow}
          size={size}
          trailingAction={trailingAction}
          // exactOptionalPropertyTypes: omit rather than pass undefined.
          {...(selection.value ? { value: `brand:${selection.value}` } : {})}
          {...(restoredQuery === undefined ? {} : { initialQuery: restoredQuery })}
          // Mount-time focus is for the first render only: a remount from resetInput
          // happens while a dialog is opening and must not take its focus.
          {...(magicNumber > 0 ? { focusOnMount: false } : {})}
        />
        {flow.element}
      </>
    );
  },
  // Per rule: no-flaky-tests (ai.knowledge/platform) — registry completion must replace Loading.
  // Keep registry, layout and identification props in sync with the caller.
  (prev, next) =>
    prev.brands === next.brands &&
    prev.models === next.models &&
    prev.selection.value === next.selection.value &&
    prev.size === next.size &&
    prev.disabled === next.disabled &&
    prev.focusOnMount === next.focusOnMount &&
    prev.initialQuery === next.initialQuery &&
    prev.onIdentified === next.onIdentified &&
    prev.onModelSelect === next.onModelSelect &&
    prev.onNotListed === next.onNotListed &&
    prev.onQueryChange === next.onQueryChange
);
